export interface ApiClientConfig {
  baseUrl: string;
  getAuthToken?: () => string | null | undefined;
  fetchImpl?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    super(`API error ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  // Surcharge ponctuelle du token porté par CET appel — prioritaire sur
  // `getAuthToken` de la config (AUT-03, voir `getMe()` dans src/auth.ts) : juste
  // après un login réussi, le token fraîchement émis n'est pas encore forcément
  // reflété par un éventuel `getAuthToken` branché sur un état React (mise à jour
  // asynchrone) — l'appelant qui a le token sous la main peut le passer directement
  // ici plutôt que d'attendre un re-render.
  accessToken?: string;
}

// AUT-30 — branchés par <AuthProvider> (packages/ui/src/auth/AuthContext.tsx) APRÈS
// le montage : `apiClient` est un singleton créé AVANT React (voir apps/*/src/
// services/api.ts), donc AVANT qu'aucun état React (setAccessToken/setUser) n'existe
// pour les alimenter — d'où ce point de branchement tardif (`setAuthRecoveryHooks`)
// plutôt qu'un paramètre du constructeur. Sans hooks branchés (ex. tout test qui
// instancie un `ApiClient` nu), le comportement 401 reste STRICTEMENT celui d'avant
// ce ticket — aucune interception.
export interface AuthRecoveryHooks {
  // Tente un rafraîchissement SILENCIEUX de la session (POST /auth/refresh) et
  // résout vers le NOUVEAU access token en cas de succès. DOIT rejeter si le
  // rafraîchissement lui-même échoue (refresh token expiré/révoqué/absent) —
  // `ApiClient` ne retente JAMAIS un rafraîchissement en échec, voir
  // `sendWithAuthRecovery` ci-dessous.
  refresh: () => Promise<string>;
  // Appelée UNE SEULE FOIS (par rafraîchissement en échec, jamais par 401 individuel
  // — voir le mutex `refreshOnce` ci-dessous) quand la tentative de rafraîchissement
  // ci-dessus a échoué : signal que la session est RÉELLEMENT terminée, pour que
  // l'appelant (AuthContext) nettoie son état d'authentification et affiche un moyen
  // de se reconnecter.
  onSessionExpired: () => void;
}

// FND-23 : `baseUrl` peut désormais être RELATIF (ex. '/api', consommé same-origin
// derrière le reverse proxy Caddy de production) — pas seulement absolu comme en
// dev/staging ('http://localhost:3030'). `new URL(path, baseUrl)` exige un second
// argument absolu (le "base" du WHATWG URL parser doit lui-même être une URL
// complète, avec schéma — sinon `TypeError: Invalid base URL`, y compris dans un
// vrai navigateur, ce n'est pas une limitation de jsdom/Node) : un `baseUrl` relatif
// est donc d'abord résolu contre `window.location.origin` avant d'être utilisé comme
// base. `apiClient` ne tourne que côté navigateur (web-admin/web-parent) — `window`
// y est toujours défini ; le repli `http://localhost` ne sert qu'aux tests (jsdom).
function resolveAbsoluteBase(baseUrl: string): string {
  if (/^https?:\/\//i.test(baseUrl)) return baseUrl;
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost';
  return new URL(baseUrl, origin).toString();
}

function buildUrl(baseUrl: string, path: string, query?: RequestOptions['query']): string {
  const absoluteBase = resolveAbsoluteBase(baseUrl);
  const url = new URL(path.replace(/^\//, ''), absoluteBase.endsWith('/') ? absoluteBase : `${absoluteBase}/`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getAuthToken?: () => string | null | undefined;
  private readonly fetchImpl: typeof fetch;
  // AUT-30 — voir setAuthRecoveryHooks()/sendWithAuthRecovery() plus bas.
  private authHooks: AuthRecoveryHooks | null = null;
  // AUT-30 — mutex : un SEUL POST /auth/refresh « en vol » à la fois, partagé par
  // toute requête concurrente qui échoue en 401 pendant qu'il est en cours (voir le
  // cas limite « condition de course » du ticket). Remis à `null` dès que la
  // tentative se règle, succès OU échec (`finally`) — le PROCHAIN 401, plus tard,
  // déclenche un tout nouvel essai, jamais un résultat mis en cache indéfiniment.
  private pendingRefresh: Promise<string> | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.getAuthToken = config.getAuthToken;
    // `.bind(globalThis)`, PAS `fetch` nu — bug réel constaté (pas une prudence
    // théorique) : appelée plus bas via `this.fetchImpl(...)`, la référence nue
    // hérite de `this = l'instance ApiClient`, pas `window`. Le `fetch` NATIF d'un
    // vrai navigateur exige `this === window` (vérification WebIDL sur les API
    // natives) et lève `TypeError: Failed to execute 'fetch' on 'Window': Illegal
    // invocation` dans ce cas précis — invisible dans TOUTE la suite de tests
    // existante (vitest/jsdom, ou tout `fetchImpl` mocké dans les tests) puisque ni
    // jsdom ni une fonction mock n'imposent cette contrainte de récepteur, seul un
    // vrai navigateur la fait respecter. Repéré en reproduisant le bug AUT-01 dans
    // un vrai navigateur (voir docs/architecture.md, section "AUT-01") — masqué par
    // un second bug CORS distinct tant que celui-ci n'était pas corrigé.
    this.fetchImpl = config.fetchImpl ?? fetch.bind(globalThis);
  }

  // AUT-30 — branché par <AuthProvider> APRÈS le montage (voir AuthRecoveryHooks
  // ci-dessus). `null` désactive l'interception (retour à l'état d'avant ce ticket).
  setAuthRecoveryHooks(hooks: AuthRecoveryHooks | null): void {
    this.authHooks = hooks;
  }

  // AUT-30 — voir le commentaire sur `pendingRefresh` ci-dessus : PAS une fonction
  // `async` (pas de `await` entre la lecture et l'écriture de `pendingRefresh`) —
  // essentiel pour le mutex, JS étant single-threaded, deux appels synchrones
  // successifs (avant toute résolution de promesse) voient forcément la même
  // instance déjà posée par le premier, jamais une fenêtre où les deux la
  // manqueraient.
  private refreshOnce(): Promise<string> {
    if (!this.authHooks) {
      return Promise.reject(new Error('AUT-30: aucun AuthRecoveryHooks configuré (setAuthRecoveryHooks).'));
    }
    if (!this.pendingRefresh) {
      const hooks = this.authHooks;
      this.pendingRefresh = hooks.refresh().finally(() => {
        this.pendingRefresh = null;
      });
    }
    return this.pendingRefresh;
  }

  // AUT-30 — enveloppe UN essai réseau (`buildInit` reconstruit les en-têtes/le
  // corps pour le TOKEN COURANT, jamais un objet `RequestInit` figé — nécessaire
  // puisque le retry utilise un NOUVEAU token). Si la réponse est un 401 sur une
  // requête qui portait déjà un token (`token` non vide — un appel SANS token,
  // ex. POST /auth/login, ne peut structurellement pas signifier "session
  // expirée" : refresh() échouerait de toute façon et forcerait à tort l'écran
  // "Se reconnecter" sur l'écran de connexion lui-même), tente EXACTEMENT UN
  // rafraîchissement (`isRetry` empêche toute récursion au-delà d'un seul essai —
  // cas limite du ticket : jamais de boucle si le rafraîchissement échoue à son
  // tour) puis REJOUE la requête avec le nouveau token — invisible pour l'appelant
  // si ça réussit. Si le rafraîchissement échoue, prévient `onSessionExpired()`
  // (UNE SEULE fois par échec réel, quel que soit le nombre de 401 concurrents —
  // garanti par le mutex `refreshOnce`) et renvoie la réponse 401 D'ORIGINE, que
  // l'appelant traite normalement (ApiError, comme avant ce ticket).
  private async sendWithAuthRecovery(
    buildInit: (token: string | null | undefined) => RequestInit,
    url: string,
    token: string | null | undefined,
    isRetry: boolean,
  ): Promise<Response> {
    const response = await this.fetchImpl(url, buildInit(token));
    if (response.status !== 401 || !token || isRetry || !this.authHooks) {
      return response;
    }
    try {
      const refreshedToken = await this.refreshOnce();
      return this.sendWithAuthRecovery(buildInit, url, refreshedToken, true);
    } catch {
      this.authHooks.onSessionExpired();
      return response;
    }
  }

  private async request<T>(method: string, path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const token = options?.accessToken ?? this.getAuthToken?.();
    // ENR-07 : un corps `FormData` (upload de fichier, multipart/form-data) ne doit
    // JAMAIS être sérialisé en JSON ni recevoir `Content-Type: application/json` —
    // le navigateur pose LUI-MÊME `Content-Type: multipart/form-data; boundary=...`
    // en fonction du contenu réel de la FormData (le boundary est généré à la volée) ;
    // le fixer à la main casserait le parsing côté serveur (boundary manquant/faux).
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const buildInit = (authToken: string | null | undefined): RequestInit => {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
      return {
        method,
        headers,
        body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
        signal: options?.signal,
        // `include` (pas le défaut `same-origin`) : requis pour POST /auth/login
        // (AUT-01) — le refresh token est un cookie httpOnly émis par apps/api, un
        // service sur un port différent de web-admin/web-parent (cross-origin du
        // point de vue de fetch, même si "same-site" au sens de SameSite=Lax — voir
        // docs/architecture.md, section "AUT-01"). Sans `include`, le navigateur
        // n'accepterait ni ne renverrait ce cookie sur une requête cross-origin,
        // quels que soient les en-têtes CORS du serveur.
        credentials: 'include',
      };
    };

    const response = await this.sendWithAuthRecovery(
      buildInit,
      buildUrl(this.baseUrl, path, options?.query),
      token,
      false,
    );

    const isJson = response.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText, payload);
    }

    return payload as T;
  }

  /**
   * SCH-02 — résout un chemin RELATIF renvoyé par l'API (ex. `SafeSchool#logoUrl`,
   * `/schools/:id/logo`) en une URL ABSOLUE directement utilisable dans un attribut
   * `<img src>`, un lien, etc. RÉUTILISE la même logique de résolution que
   * `request()`/`getBlob()` ci-dessus (`buildUrl`, module-level) — DÉLIBÉRÉMENT
   * SANS jeton d'authentification : réservé à des ressources PUBLIQUES (voir
   * `GET /schools/:id/logo`, sans `requireAuth`) — pour une ressource
   * confidentielle, utiliser `getBlob()` à la place, jamais celle-ci.
   */
  resolveAssetUrl(path: string): string {
    return buildUrl(this.baseUrl, path);
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  /**
   * ENR-07 — téléchargement d'un contenu BINAIRE (ex. document d'inscription
   * téléversé, PDF/JPG/PNG) : DÉLIBÉRÉMENT SÉPARÉ de `request()` ci-dessus, qui
   * suppose toujours une réponse JSON/texte. Le jeton reste dans l'en-tête
   * `Authorization` (JAMAIS en query string, contrairement à une URL signée
   * classique) — cohérent avec la règle déjà actée pour ce projet (AUT-01) que
   * l'access token ne doit JAMAIS se retrouver quelque part de persistant/loggé
   * (historique navigateur, logs d'accès serveur, en-tête Referer) : l'appelant
   * doit donc convertir le `Blob` renvoyé en URL d'objet local
   * (`URL.createObjectURL`) plutôt que de construire un lien direct vers cette
   * route avec le jeton en paramètre.
   */
  async getBlob(path: string, options?: RequestOptions): Promise<Blob> {
    const token = options?.accessToken ?? this.getAuthToken?.();
    // AUT-30 : MÊME interception 401 → refresh → retry que request() ci-dessus —
    // un téléchargement (reçu, document ENR-07...) authentifié depuis longtemps est
    // tout aussi exposé à l'expiration en cours de session.
    const buildInit = (authToken: string | null | undefined): RequestInit => {
      const headers: Record<string, string> = {};
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      return { method: 'GET', headers, signal: options?.signal, credentials: 'include' };
    };

    const response = await this.sendWithAuthRecovery(
      buildInit,
      buildUrl(this.baseUrl, path, options?.query),
      token,
      false,
    );

    if (!response.ok) {
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await response.json() : await response.text();
      throw new ApiError(response.status, response.statusText, payload);
    }

    return response.blob();
  }
}
